import { DiscountDTO, DiscountId, DiscountType } from 'types/discounts/discounts';
import { PaymentId } from 'types/payments/payments';
import { Address, NonNullableObject } from 'types/common';
import { settlementChildrenDTOFields } from 'consts/settlementChildren/settlementChildrenDTOFields';
import { InstitutionId } from 'types/institution';
import { VatRateType, VatReleaseReason } from 'types/settlementVatRates';
import { SettlementConfiguration } from 'types/additionalLesson/additionalLesson';
import { CourseId } from 'types/lesson';
import { ChildId, ChildMealType, ChildSettlementId } from '../child';

export type CostAllocationId = string & { readonly type: unique symbol };

export enum PaymentTypes {
    CASH = 'CASH',
    WIRE_TRANSFER = 'WIRE_TRANSFER',
    PAYU = 'PAYU',
}

export enum ChildPaymentMethod {
    CASH = 'CASH',
    BANK_TRANSFER = 'BANK_TRANSFER',
    PAYU = 'PAYU',
    BLIK = 'BLIK',
    CARD = 'CARD',
    OTHER = 'OTHER',
}

export enum ChildPaymentType {
    PAYMENT = 'PAYMENT',
    COST = 'COST',
    ADJUSTMENT = 'ADJUSTMENT',
    REVERSAL = 'REVERSAL',
}

export enum ChildPaymentSource {
    SETTLEMENT = 'SETTLEMENT',
    CORRECTION = 'CORRECTION',
    ADDITIONAL_COST = 'ADDITIONAL_COST',
    MANUAL = 'MANUAL',
}

export enum TransferTypes {
    PAYMENT = 'PAYMENT',
    REFUND = 'REFUND',
}

export enum PaymentStatusSettlement {
    IN_PROGRESS = 'IN_PROGRESS',
    CANCELLED = 'CANCELLED',
    RECORDED = 'RECORDED',
}

export enum BalanceTargetType {
    PARENT = 'PARENT',
    FOUNDATION = 'FOUNDATION',
    CATERING = 'CATERING',
}

export enum ManualBillCostCategory {
    STARTER_KIT = 'STARTER_KIT',
    OTHER = 'OTHER',
}

export type SettlementChildSimpleDTO = {
    [settlementChildrenDTOFields.id]: ChildId;
    [settlementChildrenDTOFields.lastName]: string;
    [settlementChildrenDTOFields.firstName]: string;
    [settlementChildrenDTOFields.institutionId]: InstitutionId;
};

export type SettlementChildInfoBasic = {
    agreementEnd: string;
    name: string;
    lastName: string;
    childId: string;
    groupName: string;
};

export type SettlementSummaryV2SimplePageableDto = {
    id: ChildSettlementId | null;
    childInfoBasic: SettlementChildInfoBasic;
    computed: boolean;
    stayGrossFee: number;
    mealGrossFee: number;
    lessonsGrossFee: number;
    additionalCostGrossSummary: number;
    grossSettlementSummary: number;
    childBalance: number;
    verified: boolean;
};

export type PaymentItem = {
    id: PaymentId;
    paymentType: PaymentTypes;
    status: PaymentStatusSettlement;
    amount: number;
    date: string;
};

export type ChildPayment = {
    id: PaymentId;
    date: string;
    amount: number;
    transactionType: TransferTypes;
    paymentFormType: PaymentTypes;
    balanceTargetType: BalanceTargetType;
    description: string | null;
};

export type ChildTransactionListItem = {
    id: PaymentId;
    childId: ChildId;
    institutionId: InstitutionId;
    dateTime: string;
    amount: number;
    category: BalanceTargetType;
    relatedTransactionId: PaymentId;
    description: string | null;
    type: ChildPaymentType;
    source: ChildPaymentSource;
    paymentMethod: ChildPaymentMethod;
};

export type ChildTransactionPayment = {
    id: PaymentId;
    childId: ChildId;
    institutionId: InstitutionId;
    dateTime: string;
    amount: number;
    category: BalanceTargetType;
    relatedTransactionId: PaymentId;
    description: string | null;
    type: ChildPaymentType;
    source: ChildPaymentSource;
    paymentMethod: ChildPaymentMethod;
};

export type CreateTransactionCostDto = {
    childId: ChildId;
    institutionId: InstitutionId;
    amount: number;
    category: BalanceTargetType;
    date: string;
    description: string;
};

export type CreateTransactionPaymentDto = {
    paymentRequestId: string;
    childId: ChildId;
    institutionId: InstitutionId;
    amount: number;
    category: BalanceTargetType;
    date: string;
    description: string;
    paymentMethod: ChildPaymentMethod;
};

export type UpdateTransactionRequest = {
    date: string;
    amount: number;
    description: string;
    category: BalanceTargetType;
    paymentMethod: ChildPaymentMethod;
    type: ChildPaymentType;
};

export type UpdateTransactionDto = UpdateTransactionRequest;

export type AdditionalCost = {
    id: string;
    name: string;
    costCategory: CostAllocationRefersTo;
    verified: boolean;
    issueDate: string;
    dueDate: string;
    saleDate: string;
    grossAmount: number;
    nettAmount: number;
    vatRateType: VatRateType;
    exemptionReason: VatReleaseReason;
    customExemptionReason: string | null;
    notes: string | null;
    invoiceRecipientId?: string | null;
};

export type CreateManualBillDto = {
    name: string | null;
    childId: ChildId;
    costCategory: ManualBillCostCategory;
    issueDate: string | null;
    dueDate: string | null;
    saleDate: string | null;
    nettAmount: number | null;
    vatRateType: VatRateType;
    exemptionReason: VatReleaseReason;
    customExemptionReason: string | null;
    notes: string | null;
};

export type ChildBillAdditionalComponent = {
    id: string;
    name: string;
    amountString: string;
    discountString?: string;
    vatRate: VatRateType;
    vatAmount: number;
    netAmount: number;
    grossAmount: number;
    units: number;
    unitGrossPrice: number;
};

export type ChildBillBaseComponent = {
    id: string;
    name: string;
    grossAmount: number;
    netAmount: number;
    vatAmount: number;
    discountString?: string;
    additionalComponents?: ChildBillAdditionalComponent[];
    vatRate: VatRateType;
    onInvoice: boolean;
};

export type CostAllocationType = PriceDeductionType;

export enum CostAllocationRefersTo {
    STAY = 'STAY',
    MEAL = 'MEAL',
    EXTRA_LESSONS = 'LESSON',
    OTHER = 'OTHER',
}

export type ChildCostAllocation = {
    id: CostAllocationId;
    type: CostAllocationType;
    splitType?: PriceDeductionType;
    sourceType?: ChildPaymentSource;
    courseList: string[];
    payerType: CostAllocationPayerType;
    address: NonNullableObject<Required<Address>>;
    refersTo: CostAllocationRefersTo;
    costCategory: CostAllocationRefersTo | ManualBillCostCategory;
    bankAccountNumber: string | null;
    correctionId: string | null;
    monthlySettlementId: string | null;
    additionalCostId: string | null;
    invoiceNotes: string | null;
    foundationName: string;
    nip: string;
    refersToCourses?: {
        courseId: string;
        courseName: string;
    }[];
    refersToCoursesIds?: string[];
    specificAmount: number | null;
    finalAmount: number;
    archived: boolean;
    percentageAmount: number | null;
    child?: {
        childId: ChildId;
        firstName: string;
        lastName: string;
    };
};

export enum PriceDeductionType {
    PERCENTAGE = 'PERCENTAGE',
    SPECIFIC_AMOUNT = 'SPECIFIC_AMOUNT',
}

export enum CostAllocationPayerType {
    PARENT = 'PARENT',
    FOUNDATION = 'FOUNDATION',
}

export type CostSplitMonthlySettlementPrice = { foundationName: string; foundationCost: number };

type MealDiscount = { name: string; type: DiscountType; amount: number };

type StayDiscount = { discountName: string; type: DiscountType; amount: number };

type StaySettlementPercentageDiscount = {
    id: DiscountId;
    discountName: string;
    percentage: number;
    combinable: boolean;
};

export type DayPriceSummary = {
    netPriceWithoutDiscounts: number;
    fromDate: string;
    toDate: string;
    staySettlementPercentageDiscounts: StaySettlementPercentageDiscount[];
    grossPriceWithPercentageDiscounts: number;
    discountApplied: boolean;
};

export type HoursPriceSummaryDTO = {
    paidHours: number;
    discounts: StaySettlementPercentageDiscount[];
    pricePerHourWithDiscounts: number;
    totalPriceWithDiscounts: number;
};

export type StaySettlement = {
    daysPriceSummaries: DayPriceSummary[] | null;
    hoursPriceSummaries: HoursPriceSummaryDTO[] | null;
    staySettlementNotPercentageDiscounts: StayDiscount[];
    grossCost: number;
    costVatAmount: number;
    freeHours: number;
    refundApplicable: boolean;
    grossRefund: number;
    totalGrossCost: number;
    costSplitMonthlySettlementPrices: CostSplitMonthlySettlementPrice[];
};

export type MealCost = {
    dietName: string;
    mealType: ChildMealType;
    count: number;
    baseNetPrice: number;
    grossPrice: number;
    discounts: DiscountDTO[];
    totalGrossPrice: number;
    netPrice: number;
};

export type MealSettlement = {
    mealCosts: MealCost[];
    discounts: MealDiscount[];
    grossCost: number;
    costVatAmount: number;
    grossRefund: number;
    totalGrossCost: number;
    costSplitMonthlySettlementPrices: CostSplitMonthlySettlementPrice[];
    subjectToInvoicing: boolean | null;
};

export type LessonCost = {
    courseId: CourseId;
    name: string;
    baseNetPrice: number;
    grossPrice: number;
    discounts: DiscountDTO[];
    totalGrossPrice: number;
    netPrice: number;
    vatRate: VatRateType;
    refundVatRate: VatRateType;
    netRefund: number;
    grossRefund: number;
    count?: number;
    startDate?: string;
    endDate?: string;
};

type LessonDiscount = {
    id: DiscountId;
    name: string;
    type: DiscountType;
    amount: number;
};

export type LessonSettlement = {
    lessonCosts: LessonCost[];
    discounts: LessonDiscount[];
    grossCost: number;
    costVatAmount: number;
    grossRefund: number;
    totalGrossCost: number;
    costSplitMonthlySettlementPrices: CostSplitMonthlySettlementPrice[];
};

export type MonthlySettlementComponentRowDto = {
    name: string;
    courseId: CourseId | null;
    courseName: string | null;
    unitNetPrice: number;
    netCostSummary: number;
    unitGrossPrice: number;
    grossSummary: number;
    units: number;
    vatRate: VatRateType;
    vatExemptionReason: string | null;
    vatSummary: number;
    discountName: string | null;
    categoryDiscountName: string | null;
    categoryDiscountValue: number;
};

export type MonthlySettlementComponentDto = {
    componentType: CostAllocationRefersTo;
    name: string;
    netCostSummary: number;
    grossSummary: number;
    vatSummary: number;
    vatRate: VatRateType;
    vatExemptionReason: string | null;
    rows: MonthlySettlementComponentRowDto[];
    discountName: string | null;
    discountValue: number;
    componentDiscountApplied: boolean;
    discountApplied: boolean;
    paymentType: SettlementConfiguration | null;
    onInvoice: boolean;
};

export type MonthlySettlementCostSplitCourseDto = {
    courseId: CourseId;
    courseName: string;
};

export type MonthlySettlementCostSplitDto = {
    id: string;
    institutionId: string;
    monthlySettlementId: string;
    correctionId: string | null;
    foundationName: string;
    nip: string;
    address: NonNullableObject<Required<Address>>;
    bankAccountNumber: string | null;
    costCategory: CostAllocationRefersTo | ManualBillCostCategory;
    courseList: MonthlySettlementCostSplitCourseDto[];
    splitType: PriceDeductionType;
    entryGrossAmount: number;
    percentageAmount: number | null;
    finalGrossAmount: number;
    payerType: CostAllocationPayerType;
    invoiceNotes: string | null;
    verified: boolean;
    invoiceData: SummaryInvoiceDataDto;
};

export type MonthlySettlementCorrectionDto = {
    id: string;
    orderNumber: number;
    grossDifferenceList: Array<{
        grossDifference: number;
        name: string;
    }>;
    grossSummary: number;
    grossAmountDifference: number;
    verified: boolean;
    verificationDate: string;
    costSplits: MonthlySettlementCostSplitDto[];
    costComponentList: MonthlySettlementComponentDto[];
    filePath?: string;
    fileName?: string;
};

export type MonthlySettlementDetailsDto = {
    id: ChildSettlementId;
    grossSummary: number;
    netSummary: number;
    costComponentList: MonthlySettlementComponentDto[];
    computed: boolean;
    paid: boolean;
    verified: boolean;
    verificationDate: string;
    costSplits: MonthlySettlementCostSplitDto[];
    invoiceData: SummaryInvoiceDataDto;
};

export type ChildSettlementV2DTO = {
    monthlySettlementDetailsDto: MonthlySettlementDetailsDto;
    additionalCostSummaryListDto?: AdditionalCostSummariesDto;
    corrections: MonthlySettlementCorrectionDto[];
};

export enum CorrectionSubjectType {
    CHILD_SETTLEMENT = 'CHILD_SETTLEMENT',
    COST_SPLIT = 'COST_SPLIT',
}

export type MonthlySettlementSimpleDTO = {
    id: ChildSettlementId | null;
    period: string;
    childId: ChildId;
    institutionId: InstitutionId;
    grossSummary: number | null;
    netSummary: number | null;
    costComponentList: MonthlySettlementComponentDto[];
    computed: boolean;
    verified: boolean;
    verificationDate: string | null;
};

export type AddCostSplitRequestDto = {
    foundationName: string;
    nip: string;
    address: NonNullableObject<Required<Address>>;
    bankAccountNumber: string | null;
    invoiceNotes: string | null;
    courseIds?: string[];
    entryGrossAmount: number | null;
    percentageAmount: number | null;
    monthlySettlementId: ChildSettlementId | null;
    correctionId: string | null;
    additionalCostId?: string | null;
    costCategory: string | null;
    splitType: PriceDeductionType;
    payerType: CostAllocationPayerType;
    sourceType: ChildPaymentSource;
    courseIdProvided: boolean;
    amountFilled: boolean;
};

export type EditChildCostAllocation = {
    monthlySettlementId: string | null;
    correctionId: string | null;
    additionalCostId: string | null;
    foundationName: string | null;
    nip: string | null;
    address: {
        street: string | null;
        zipCode: string | null;
        city: string | null;
    };
    bankAccountNumber: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    costCategory: any;
    courseIds: Array<string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    splitType: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payerType: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sourceType: any;
    entryGrossAmount: number | null;
    percentageAmount: number | null;
    invoiceNotes: string | null;
    courseIdProvided: boolean | null;
    amountFilled: boolean | null;
};

type InstitutionTransferInformationDTO = {
    recipientName: string | null;
    bankAccountNumber: string | null;
    amount: number;
};

type CateringTransferInformationDTO = {
    transferTitle: string | null;
    recipientName: string | null;
    bankAccountNumber: string | null;
    amount: number;
};

export type SettlementTransferInformationDTO = {
    priceTotal: number;
    month: { year: number; monthValue: number };
    childSimpleDto: SettlementChildSimpleDTO;
    institutionTransferInformationDto: InstitutionTransferInformationDTO;
    cateringTransferInformationDto: CateringTransferInformationDTO | null;
};

export type ChildSettlementBalanceDTO = {
    childId: ChildId;
    parentBalance: number;
    foundationBalance: number;
    generalBalance: number;
    cateringBalance: number;
};

export type AdditionalCostSummariesDto = {
    additionalCostSummaryDtos: Array<AdditionalCostSummaryDto>;
};

export type AdditionalCostSummaryDto = {
    detailsDto: AdditionalCostDetailsDto;
    corrections: Array<AdditionalCostCorrectionDetailsDto>;
};

export type AdditionalCostDetailsDto = {
    id: string;
    name: string;
    costCategory: ManualBillCostCategory;
    createdDate: string;
    issueDate: string;
    dueDate: string;
    saleDate: string;
    grossAmount: number;
    nettAmount: number;
    vatAmount: number;
    vatRateType: VatRateType;
    exemptionReason: VatReleaseReason;
    customExemptionReason: string;
    notes: string;
    paid: boolean;
    verified: boolean;
    verificationDate: string;
    costSplits: Array<AdditionalCostCostSplitDetailsDto>;
    invoiceData: SummaryInvoiceDataDto;
};

export type AdditionalCostCostSplitDetailsDto = {
    id: string;
    institutionId: string;
    additionalCostId: string;
    correctionId: string;
    foundationName: string;
    nip: string;
    address: FoundationAddressDto;
    bankAccountNumber: string;
    costCategory: CostAllocationRefersTo;
    courseList: Array<CourseInfoDto>;
    splitType: PriceDeductionType;
    entryGrossAmount: number;
    percentageAmount: number;
    finalGrossAmount: number;
    payerType: CostAllocationPayerType;
    invoiceNotes: string;
    sourceName: string;
    invoiceData?: SummaryInvoiceDataDto;
};

export type CourseInfoDto = {
    courseId: string;
    courseName: string;
};

export type FoundationAddressDto = {
    street: string;
    zipCode: string;
    city: string;
};

export type SummaryInvoiceDataDto = {
    id: string;
    invoiceNumber: string;
    issueDate: string;
};

export type AdditionalCostCorrectionDetailsDto = {
    id: string;
    additionalCostId: string;
    orderNumber: number;
    grossDifferenceList: Array<SettlementSummaryCorrectionDifference>;
    grossSummary: number;
    grossAmountDifference: number;
    verified: boolean;
    verificationDate: string;
    costSplits: Array<AdditionalCostCostSplitDetailsDto>;
    invoiceData: Array<SummaryInvoiceDataDto>;
    filePath?: string;
    fileName?: string;
};

export type SettlementSummaryCorrectionDifference = {
    grossDifference: number;
    name: string;
};
